/**
 * @file pricingHelpers.js
 * @version 1.7.1
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-05-07
 * @modified 2026-10-28
 * @description Shared helpers for the contract-pricing modals + supplier
 *              autofill. Originally lived inline inside ContractPricingModal —
 *              extracted so straights / blends / compounds / spot (and now
 *              ordering / invoicing / billing / purchase orders) all auto-fill
 *              cost + supplier the same way when a product is selected.
 */

/**
 * Pick a default supplier for a product based on the leading characters of its
 * item_code. Current rule (system-wide — applied anywhere a product is picked
 * and a supplier can be suggested; the user can always override):
 *   100…            → Mole Valley Feed Solutions
 *   2…              → NWF Agriculture
 *   K… or P…        → ForFarmers
 *   anything else   → no suggestion (null)
 * Returns the matching supplier object from `suppliers` or null.
 *
 * The Mole Valley match prefers the canonical "Mole Valley Feed Solutions"
 * contact (account_no 0180), falling back to any "feed solutions" variant
 * (e.g. a trailing-period dupe or a branch contact) if the canonical row is
 * missing. Deliberately does NOT fall back to "Mole Valley Farmers".
 */
export function autoSelectSupplierFromItemCode(itemCode, suppliers) {
  if (!itemCode || !Array.isArray(suppliers) || suppliers.length === 0) return null;
  const code = String(itemCode).trim();
  const first = code.charAt(0).toUpperCase();
  const nameLower = (s) => (s.company_name || '').toLowerCase();

  if (code.startsWith('100')) {
    return suppliers.find(s => s.company_name === 'Mole Valley Feed Solutions')
        || suppliers.find(s => nameLower(s).includes('mole valley feed solutions'))
        || null;
  }
  if (first === '2') {
    return suppliers.find(s => nameLower(s).startsWith('nwf agri'))
        || suppliers.find(s => nameLower(s).includes('nwf'))
        || null;
  }
  if (first === 'K' || first === 'P') {
    return suppliers.find(s => nameLower(s).includes('forfarmers')) || null;
  }
  return null;
}

/**
 * Return a numeric cost from a product record, or null when missing.
 * Postgres NUMERIC columns come back as strings — handle both.
 */
export function getProductCost(product) {
  if (!product) return null;
  const c = product.cost;
  if (c === null || c === undefined || c === '') return null;
  const n = parseFloat(c);
  return Number.isFinite(n) ? n : null;
}

/**
 * Default margin (£) added on top of cost (and any per-category extras like
 * additive cost or haulage) when the modal computes a suggested price.
 *   1 (Straights): no suggestion offered — the per-month grid is too varied.
 *   2 (Blends), 3 (Compounds), 5 (Spot): cost + additive + £40
 *   4 (Minerals): cost + haulage + £150
 *   6 (Milk Powder): no suggestion offered — there is no typical margin.
 * Edit this constant to change defaults — every pricing modal reads from it.
 */
export const MARGIN_DEFAULTS = {
  2: 40,
  3: 40,
  4: 150,
  5: 40
};

/**
 * Compute a suggested price for a pricing modal.
 *   - Straights (1) and any unknown category return null (no suggestion).
 *   - Mineral (4) adds haulage on top of cost (haulage isn't part of cost).
 *   - Everywhere else (Blends, Compounds, Spot): cost + margin. The additive
 *     price is rolled into the cost field by the modal itself, so we don't
 *     add margin to it twice.
 *   - Returns null when cost ≤ 0 or no margin default is configured.
 */
export function computeSuggestedPrice(categoryId, { cost = 0, haulage = 0 } = {}) {
  const c = parseFloat(cost) || 0;
  if (c <= 0) return null;
  const margin = MARGIN_DEFAULTS[categoryId];
  if (margin === undefined) return null;
  if (categoryId === 4) {
    return c + (parseFloat(haulage) || 0) + margin;
  }
  return c + margin;
}

/*
 * The fulfilment badge (FULFILLED / UTILISE / UNFULFILLED) and its group
 * roll-up live in contractUsage.js since 2026-10-28, shared with the server
 * (the reps' contracts view reads the same rule). Re-exported here so the
 * contract screens' imports are unchanged.
 */
export { contractFulfilmentStatus, aggregateFulfilmentStatus } from './contractUsage';

/**
 * Compute the cost of a contract line: product cost + additive price (if any).
 * Used both by the modals (when the user picks a product/additive) and by
 * the listing-screen "refresh cost from inventory" action.
 */
export function computeCostWithAdditive(productCost, additivePrice) {
  const c = productCost === null || productCost === undefined || productCost === '' ? 0 : parseFloat(productCost);
  const a = additivePrice === null || additivePrice === undefined || additivePrice === '' ? 0 : parseFloat(additivePrice);
  if (!Number.isFinite(c) && !Number.isFinite(a)) return null;
  return (Number.isFinite(c) ? c : 0) + (Number.isFinite(a) ? a : 0);
}

/**
 * Is a contract's date range the period we are in right now?
 *
 * Used to decide whether an inventory-cost mismatch is worth flagging. On a
 * past or future period it is not: the contract was priced against the cost of
 * its own period, and the item's cost today has no bearing on it — telling
 * someone their 2025 contract "disagrees with inventory" is noise that trains
 * them to ignore the warning that matters.
 *
 * Plain string comparison on YYYY-MM-DD. These are date columns, not
 * timestamps, and parsing them into Date objects reintroduces the timezone
 * drift around midnight that todayISO() in periodHelper exists to avoid.
 */
export function isCurrentPeriod(startDate, endDate) {
  if (!startDate || !endDate) return false;
  const iso = (v) => (typeof v === 'string' ? v : new Date(v).toISOString()).slice(0, 10);
  const d = new Date();
  const today = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  return iso(startDate) <= today && today <= iso(endDate);
}

/**
 * Does this contract's OWN date range cover today?
 *
 * The tables' "current" tick used to follow the period alone, so two contracts
 * splitting one period (Westcott Farm Partnership's compounds, Winter 2026-27:
 * 01/10/26–31/01/27 and 01/02/27–30/04/27) were both ticked. A contract with
 * dates of its own is current only inside them; one without falls back to its
 * period (Cam, 2026-10-22).
 */
export function contractCoversToday(contract) {
  if (!contract?.start_date || !contract?.end_date) return true;
  return isCurrentPeriod(contract.start_date, contract.end_date);
}

/**
 * What the inventory item says this line should cost, additive included.
 *
 * Mirrors exactly what the modal auto-fills, so a comparison against the
 * entered cost is like-for-like. Anything else would flag every line that
 * carries an additive.
 */
export function inventoryCostFor(product, additive) {
  const base = getProductCost(product);
  if (base === null) return null;
  const additivePrice = additive && additive.price ? parseFloat(additive.price) || 0 : 0;
  return computeCostWithAdditive(base, additivePrice);
}

/**
 * Does the entered cost disagree with the inventory item's, enough to say so?
 *
 * Returns the inventory figure when it is worth flagging, otherwise null.
 *
 * Null and zero are NOT a disagreement: an item with no cost recorded has
 * nothing to disagree with, and zero is how "not priced yet" is stored. Warning
 * on those would put a notice on most of the catalogue.
 *
 * Compared at 2dp rather than with a tolerance. These are money values entered
 * through a step="0.01" field, so "different" means different to the penny —
 * and an epsilon comparison sitting exactly on the boundary is at the mercy of
 * float representation, which is not a thing to hang a warning on.
 */
export function costDiffersFromInventory(enteredCost, inventoryCost) {
  if (inventoryCost === null || inventoryCost === undefined) return null;
  const inv = parseFloat(inventoryCost);
  if (!Number.isFinite(inv) || inv === 0) return null;
  const entered = parseFloat(enteredCost);
  if (!Number.isFinite(entered)) return null;
  const pence = (n) => Math.round(n * 100);
  return pence(entered) !== pence(inv) ? inv : null;
}
